import { useMemo } from 'react'
import moment from 'moment'
import type { DrinkEntry, SessionStats, AwarenessData, Drink, SoberStreak } from '@/types'
import {
  getCurrentBAC,
  getHoursUntilSober,
  getSecondsSinceLastDrink,
  countSessions,
  countCurrentSessionDrinks,
  METABOLISM_RATE,
} from '@/utils/bac'
import { computeRiskAssessment, type RiskAssessment } from '@/utils/health'

interface TotalStats {
  totalDrinks: number
  totalMl: number
  totalCalories: number
}

interface UseDrinkLogResult {
  currentBAC: number
  secondsSinceLastDrink: number
  hoursUntilSober: number
  sessionCount: number
  currentSessionDrinks: number
  currentSessionStats: SessionStats | null
  lastSessionStats: SessionStats | null
  totalStats: TotalStats
  awarenessData: AwarenessData | null
  soberStreaks: SoberStreak[]
  allSessions: SessionStats[]
  riskAssessment: RiskAssessment | null
  sortedDrinks: { myDrinks: Drink[]; allDrinks: Drink[] }
}

export function useDrinkLog(
  entries: DrinkEntry[],
  drinks: Drink[],
  databaseDrinks: Drink[],
  tick: number // Used to trigger recalculation
): UseDrinkLogResult {
  // Current BAC (recalculates on tick)
  const currentBAC = useMemo(() => {
    return getCurrentBAC(entries)
  }, [entries, tick])

  // Seconds since last drink
  const secondsSinceLastDrink = useMemo(() => {
    return getSecondsSinceLastDrink(entries)
  }, [entries, tick])

  // Hours until sober
  const hoursUntilSober = useMemo(() => {
    return getHoursUntilSober(currentBAC)
  }, [currentBAC])

  // Session count
  const sessionCount = useMemo(() => {
    return countSessions(entries)
  }, [entries])

  // Current session drink count
  const currentSessionDrinks = useMemo(() => {
    return countCurrentSessionDrinks(entries)
  }, [entries])

  // Current session stats
  const currentSessionStats = useMemo((): SessionStats | null => {
    if (currentSessionDrinks === 0) return null

    const nonGapEntries = entries.filter(e => !e.is_gap)
    const sessionEntries = nonGapEntries.slice(-currentSessionDrinks)

    return {
      drinkCount: currentSessionDrinks,
      totalMl: sessionEntries.reduce((sum, e) => sum + Number(e.drink.milliliters), 0),
      totalCalories: sessionEntries.reduce((sum, e) => sum + Number(e.drink.calories || 0), 0),
      peakBac: Math.max(...sessionEntries.map(e => e.bac || 0)),
      drinks: sessionEntries,
    }
  }, [entries, currentSessionDrinks])

  // Total stats (all time)
  const totalStats = useMemo((): TotalStats => {
    const nonGapEntries = entries.filter(e => !e.is_gap)
    return {
      totalDrinks: nonGapEntries.length,
      totalMl: nonGapEntries.reduce((sum, e) => sum + Number(e.drink.milliliters), 0),
      totalCalories: nonGapEntries.reduce((sum, e) => sum + Number(e.drink.calories || 0), 0),
    }
  }, [entries])

  // Build all sessions (shared between lastSessionStats, awarenessData, soberStreaks)
  const allSessions = useMemo((): SessionStats[] => {
    const nonGapEntries = entries.filter(e => !e.is_gap)
    if (nonGapEntries.length === 0) return []

    const sessions: SessionStats[] = []
    let tempSession: SessionStats = { drinkCount: 0, totalMl: 0, totalCalories: 0, peakBac: 0, drinks: [] }

    for (let i = 0; i < nonGapEntries.length; i++) {
      const entry = nonGapEntries[i]

      if (i > 0) {
        const prevEntry = nonGapEntries[i - 1]
        const timeToSober = (prevEntry.bac || 0) / METABOLISM_RATE
        const soberAt = moment(prevEntry.finished_at).add(timeToSober, 'hours')

        if (moment(entry.finished_at).isAfter(soberAt)) {
          if (tempSession.drinkCount > 0) {
            sessions.push(tempSession)
          }
          tempSession = { drinkCount: 0, totalMl: 0, totalCalories: 0, peakBac: 0, drinks: [] }
        }
      }

      tempSession.drinkCount++
      tempSession.totalMl += Number(entry.drink.milliliters)
      tempSession.totalCalories += Number(entry.drink.calories || 0)
      tempSession.peakBac = Math.max(tempSession.peakBac, Number(entry.bac || 0))
      tempSession.drinks.push(entry)
    }

    // Push the final in-progress/current session
    if (tempSession.drinkCount > 0) {
      sessions.push(tempSession)
    }

    return sessions
  }, [entries])

  // Last session stats (for comparison)
  // If currently drinking, second-to-last session; if sober, last completed session
  const lastSessionStats = useMemo((): SessionStats | null => {
    if (allSessions.length < 2) return null

    if (currentBAC > 0) {
      // Currently drinking — second-to-last is the last completed session
      return allSessions[allSessions.length - 2]
    }
    // Sober — last session is the most recent completed session
    return allSessions[allSessions.length - 1]
  }, [allSessions, currentBAC])

  // Awareness bar data - compares current session to historical patterns
  const awarenessData = useMemo((): AwarenessData | null => {
    // Only show when actively drinking (2+ drinks in current session)
    if (currentBAC <= 0 || currentSessionDrinks < 2) {
      return null
    }

    const nonGapEntries = entries.filter(entry => !entry.is_gap)
    if (nonGapEntries.length < 5) {
      return null
    }

    // Historical sessions = all but the last (current) session
    const historicalSessions = allSessions.slice(0, -1)

    if (historicalSessions.length < 3) {
      return null
    }

    // Get last session for direct comparison
    const lastSession = historicalSessions[historicalSessions.length - 1]

    // Calculate historical BAC averages
    const avgPeakBac = historicalSessions.reduce((sum, s) => sum + s.peakBac, 0) / historicalSessions.length
    const maxHistoricalBac = Math.max(...historicalSessions.map(s => s.peakBac))
    const lastPeak = lastSession.peakBac
    const fmt = (bac: number) => bac.toFixed(3) + '%'

    // Determine level and generate message
    let level: AwarenessData['level'] = 'success'
    let message = ''

    if (currentBAC >= 0.2) {
      level = 'dark'
      message = `Last session peaked at ${fmt(lastPeak)}. Stop now and seek help.`
    } else if (currentBAC >= 0.15) {
      level = 'danger'
      if (currentBAC > lastPeak) {
        message = `Last session peaked at ${fmt(lastPeak)}. You've passed it — stop now.`
      } else {
        message = `Last session peaked at ${fmt(lastPeak)}. Stop now — you've nearly matched it.`
      }
    } else if (currentBAC >= 0.1) {
      level = 'danger'
      if (currentBAC > lastPeak) {
        message = `Last session peaked at ${fmt(lastPeak)}. Stop now and aim lower next time.`
      } else {
        message = `Last session peaked at ${fmt(lastPeak)}. Stop now and you're doing better!`
      }
    } else if (currentBAC > maxHistoricalBac) {
      level = 'danger'
      message = `Your worst ever was ${fmt(maxHistoricalBac)}. Stop now — you've already set a new record.`
    } else if (currentBAC > lastPeak) {
      level = 'danger'
      message = `Last session peaked at ${fmt(lastPeak)}. Stop now and aim lower next time.`
    } else if (currentBAC >= lastPeak * 0.9) {
      level = 'warning'
      message = `Last session peaked at ${fmt(lastPeak)}. Stop now to beat it!`
    } else if (currentBAC >= avgPeakBac) {
      level = 'warning'
      message = `Last session peaked at ${fmt(lastPeak)}. You're above your average — stop now to do better.`
    } else {
      level = 'success'
      message = `Avg peak is ${fmt(avgPeakBac)}. You're tracking below your average.`
    }

    return {
      level,
      message,
      currentBac: currentBAC,
      avgPeakBac: Math.round(avgPeakBac * 1000) / 1000,
      lastSessionPeakBac: lastPeak,
    }
  }, [entries, allSessions, currentBAC, currentSessionDrinks])

  // Sober streaks between sessions (top 3, sorted by duration desc)
  // Excludes streaks that overlap with gap entries (unknown tracking periods)
  const soberStreaks = useMemo((): SoberStreak[] => {
    if (allSessions.length === 0) return []

    // Collect gap periods for overlap checking
    const gaps = entries.filter(e => e.is_gap).map(e => ({
      start: new Date(e.started_at || e.finished_at).getTime(),
      end: new Date(e.finished_at).getTime(),
    }))

    const overlapsGap = (streakStart: number, streakEnd: number): boolean => {
      return gaps.some(g => g.start < streakEnd && g.end > streakStart)
    }

    const streaks: SoberStreak[] = []
    const now = new Date()

    for (let i = 0; i < allSessions.length - 1; i++) {
      const session = allSessions[i]
      const nextSession = allSessions[i + 1]
      const lastDrink = session.drinks[session.drinks.length - 1]
      const timeToSober = (lastDrink.bac || 0) / METABOLISM_RATE
      const soberAt = moment(lastDrink.finished_at).add(timeToSober, 'hours')
      const nextStart = moment(nextSession.drinks[0].finished_at)

      const durationSeconds = nextStart.diff(soberAt, 'seconds')
      if (durationSeconds > 0 && !overlapsGap(soberAt.valueOf(), nextStart.valueOf())) {
        streaks.push({
          startedAt: soberAt.toISOString(),
          endedAt: nextSession.drinks[0].finished_at,
          durationSeconds,
        })
      }
    }

    // If currently sober, add a live streak from last drink to now
    if (currentBAC <= 0 && allSessions.length > 0) {
      const lastSession = allSessions[allSessions.length - 1]
      const lastDrink = lastSession.drinks[lastSession.drinks.length - 1]
      const streakStart = moment(lastDrink.finished_at)

      if (moment(now).isAfter(streakStart) && !overlapsGap(streakStart.valueOf(), now.getTime())) {
        const durationSeconds = moment(now).diff(streakStart, 'seconds')
        streaks.push({
          startedAt: streakStart.toISOString(),
          endedAt: now.toISOString(),
          durationSeconds,
          isLive: true,
        })
      }
    }

    // Sort descending by duration, take top 5, but always keep the live streak
    streaks.sort((a, b) => b.durationSeconds - a.durationSeconds)
    const top = streaks.slice(0, 5)
    const live = streaks.find(s => s.isLive)
    if (live && !top.includes(live)) top.push(live)
    return top
  }, [allSessions, entries, currentBAC, tick])

  // Risk assessment (NHMRC guidelines)
  const riskAssessment = useMemo((): RiskAssessment | null => {
    return computeRiskAssessment(entries, allSessions)
  }, [entries, allSessions])

  // Sorted drinks for autocomplete
  const sortedDrinks = useMemo(() => {
    const myDrinks: Drink[] = []
    const seenMyDrinks = new Set<string>()

    // Iterate entries in reverse to get most recent first
    const nonGapEntries = entries.filter(e => !e.is_gap)
    for (let i = nonGapEntries.length - 1; i >= 0; i--) {
      const entry = nonGapEntries[i]
      const drinkKey = entry.drink.drink
      if (!seenMyDrinks.has(drinkKey)) {
        seenMyDrinks.add(drinkKey)
        myDrinks.push(entry.drink)
      }
    }

    // Add drinks from saved templates that haven't been used yet
    for (const drink of drinks) {
      if (!seenMyDrinks.has(drink.drink)) {
        seenMyDrinks.add(drink.drink)
        myDrinks.push(drink)
      }
    }

    return {
      myDrinks,
      allDrinks: databaseDrinks,
    }
  }, [entries, drinks, databaseDrinks])

  return {
    currentBAC,
    secondsSinceLastDrink,
    hoursUntilSober,
    sessionCount,
    currentSessionDrinks,
    currentSessionStats,
    lastSessionStats,
    totalStats,
    awarenessData,
    soberStreaks,
    allSessions,
    riskAssessment,
    sortedDrinks,
  }
}
